import { createElement, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FolderInput } from 'lucide-react'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useIntroNote } from '@/src/renderer/lib/intro'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { moveTasks, type MoveResult } from '../utils/moveTasks'
import { newProjectInput } from '../utils/newProjectInput'
import type { MoveMenu } from '../utils/moveMenu'

/**
 * Der Wert für `MoveMenuContext` (#257): verschiebt die angeklickte Aufgabe — oder die ganze
 * Auswahl, wenn sie dazugehört, wie jede Zeile des Aufgaben-Menüs. Danach eine Meldung mit
 * Rückgängig: Die Aufgabe verschwindet aus der Liste, ein Fehlgriff muss umkehrbar sein. Davor
 * „Anzeigen“ (#259): springt zur ersten an ihrem neuen Platz — und hat den Fokus (#264), denn Verschieben
 * löst der Nutzer immer selbst aus: Enter springt hin.
 *
 * @param openAtRow öffnet das Panel an der Zeile (`menuShortcut` der Seite, #263) — der Weg aus dem Rechtsklick
 */
export function useMoveMenu(getSelection: () => ReadonlySet<string>, openAtRow?: (taskId: string) => void): MoveMenu {
  const { t } = useTranslation()
  const source = useDataSource()
  const noteIntro = useIntroNote()
  const getSelectionRef = useRef(getSelection)
  getSelectionRef.current = getSelection
  const openAtRowRef = useRef(openAtRow)
  openAtRowRef.current = openAtRow

  return useMemo<MoveMenu>(() => {
    const idsFor = (taskId: string, ids?: readonly string[]) => {
      if (ids) return [...ids]
      const selection = getSelectionRef.current()
      return selection.size > 1 && selection.has(taskId) ? [...selection] : [taskId]
    }

    const announce = (result: MoveResult, place: string, extra: { description?: string; undoMore?: () => Promise<void> } = {}) => {
      const count = result.moved.length
      showUndoToast({
        message: count === 1 ? t('taskItem.move.moved', { place }) : t('taskItem.move.movedMultiple', { count, place }),
        description: extra.description,
        undoLabel: t('common.undo'),
        undoneMessage: t('taskItem.move.undone'),
        icon: createElement(FolderInput, { size: 18 }),
        focusAction: true,
        onUndo: async () => {
          await source.updateTasks(result.before)
          await extra.undoMore?.()
        },
        secondaryAction: {
          label: t('common.show'),
          // Frisch lesen: `moved` hält den alten Platz, und in den Sekunden bis zum Klick kann sie weitergewandert sein.
          onClick: async () => {
            const task = await source.getTask(result.moved[0]!.id)
            if (task && !task.deletedAt) dispatchAppEvent('open-task', { task })
          },
        },
      })
    }

    return {
      move(taskId, target, place, ids) {
        void moveTasks(source, idsFor(taskId, ids), target).then(result => {
          if (result) announce(result, place)
        })
      },

      moveToNewProject(taskId, { name, lifeSpace }, ids) {
        void (async () => {
          const project = await source.createProject(newProjectInput(name, lifeSpace))
          const result = await moveTasks(source, idsFor(taskId, ids), { projectId: project.id, sectionId: null, lifeSpace })
          // Nichts gewandert (nur Unteraufgaben gewählt): kein leeres Projekt zurücklassen.
          if (!result) {
            await source.deleteProject(project.id)
            return
          }
          noteIntro('projects')
          announce(result, name, {
            description: t('taskItem.move.newProjectCreated', { space: t(`lifeSpaces.${lifeSpace}`) }),
            // Eine Handlung, ein Rückgängig — aber was inzwischen darin liegt, bleibt samt Projekt.
            undoMore: async () => {
              if (await source.getProjectIsEmpty(project.id)) await source.deleteProject(project.id)
            },
          })
        })()
      },

      async countMoving(taskId, ids) {
        // Wie `moveTasks` filtert: nur lebende Aufgaben ohne Eltern.
        const rows = await Promise.all(idsFor(taskId, ids).map(id => source.getTask(id)))
        return rows.filter(row => row && !row.deletedAt && row.parentTaskId == null).length
      },

      open(taskId) {
        openAtRowRef.current?.(taskId)
      },
    }
  }, [source, t, noteIntro])
}
